import React, { useContext, useEffect, useMemo, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import { Button } from '@/components/atoms/button'
import { ReportType } from '@/models/sales-manager/detail'
import * as api from '@/apis/aipass'
import { LoaderContextCreator } from '@/contexts/loader'
import { DetailCard, DetailCardTitle } from '@/components/molecules/sales-manager/detail-card'
import { useSalesEditGuard } from '@/hooks/use-sales-edit-guard'

type ReportProps = {
  salesManagerDailyId: string
  isConfirm: boolean
}

export const Report: React.FC<ReportProps> = props => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { isSalesReadOnly } = useSalesEditGuard()
  const [reports, setReports] = useState<ReportType[]>([])
  const [inputReport, setInputReport] = useState<string>('')

  const fetchSalesReport = async () => {
    const res = await api.fetchSalesReport('daily', props.salesManagerDailyId, '')
    setReports(res?.reports)
  }

  const onClickSaveReport = async () => {
    if (isSalesReadOnly) {
      alert(t('HotelPms.Error.readOnly'))
      return
    }
    if (inputReport === '') {
      return
    }
    setIsLoading(true)
    await api.updateSalesReport('daily', props.salesManagerDailyId, '', inputReport)
    await fetchSalesReport()
    setInputReport('')
    setIsLoading(false)
  }

  const isReportChanged = useMemo(() => inputReport.length > 0, [inputReport])

  useEffect(() => {
    fetchSalesReport()
  }, [props.salesManagerDailyId])

  return (
    <DetailCard>
      <DetailCardTitle>{t('Daily report')}</DetailCardTitle>
      <div css={(reports?.length === 0 || !props.isConfirm) && sectionStyle} />
      {!props.isConfirm && !isSalesReadOnly && (
        <div css={reportWrapperStyle}>
          <TextareaAutosize
            placeholder={t('Add daily report')}
            value={inputReport}
            onChange={v => setInputReport(v.target.value)}
            minRows={4}
          />
          {isReportChanged && (
            <div css={saveButtonWrapperStyle}>
              <Button width={80} height={38} buttonType={1} onClick={onClickSaveReport}>
                {t('Save')}
              </Button>
            </div>
          )}
        </div>
      )}
      {!!reports?.length &&
        reports.map((report, index) => (
          <div key={index} css={reportSectionStyle}>
            <p css={reportTimeStyle}>{dayjs(report?.createdAt).format('YYYY/MM/DD HH:mm')}</p>
            <div css={reportTextWrapperStyle}>
              <p
                css={reportTextStyle}
                dangerouslySetInnerHTML={{
                  __html: report?.letter?.replace(/[\n\r]/g, '<br />'),
                }}
              />
            </div>
          </div>
        ))}
      {reports?.length === 0 && !!props.isConfirm && <div css={reportEmptySectionStyle} />}
    </DetailCard>
  )
}

const reportWrapperStyle = css({
  padding: '24px 32px',
  textarea: {
    width: '100%',
    height: 130,
    border: '1px solid #cccccc',
    borderRadius: 12,
    marginBottom: 16,
    resize: 'none',
    padding: 16,
    fontSize: 14,
    '::placeholder': {
      color: '#cccccc',
    },
  },
})

const sectionStyle = css({
  borderBottom: '1px solid #f2f2f2',
})

const reportSectionStyle = css({
  backgroundColor: '#FFF',
  borderTop: '1px solid #f2f2f2',
})

const reportEmptySectionStyle = css({
  height: 185,
})

const reportTimeStyle = css({
  padding: '24px 32px 0',
  fontSize: 12,
  color: '#676767',
})

const reportTextWrapperStyle = css({
  margin: '16px 32px 0 32px',
  lineHeight: '22px',
  paddingBottom: 24,
})

const reportTextStyle = css({
  fontSize: 14,
  color: '#272727',
  lineHeight: '22px',
  fontWeight: 400,
  margin: 0,
  wordBreak: 'break-word',
})

const saveButtonWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
})
